import { videoApi, clipApi } from '../services/api';
import { auth } from '../services/firebase';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

interface BatchRequest {
  key: string;
  resolve: (data: any) => void;
  reject: (error: any) => void;
  timestamp: number;
}

class ApiOptimizer {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, Promise<any>>();
  private batchQueue = new Map<string, BatchRequest[]>();
  private batchTimers = new Map<string, NodeJS.Timeout>();
  
  // Cache TTL in milliseconds
  private readonly cacheTTL = {
    videos: 5 * 60 * 1000,      // 5 minutes
    clips: 3 * 60 * 1000,       // 3 minutes
    clip: 5 * 60 * 1000,        // 5 minutes for individual clips
    stats: 10 * 60 * 1000,      // 10 minutes
    analytics: 10 * 60 * 1000,  // 10 minutes
    video_status: 30 * 1000,    // 30 seconds
    exports: 2 * 60 * 1000,     // 2 minutes
    thumbnails: 30 * 60 * 1000, // 30 minutes
  };

  /**
   * Get data with caching and deduplication
   */
  async getCached<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    // Check cache first
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.expiry) {
      // console.log(`📋 Cache hit for ${key}`);
      return cached.data;
    }

    // Check if request is already pending (deduplication)
    const pending = this.pendingRequests.get(key);
    if (pending) {
      // console.log(`⏳ Deduplicating request for ${key}`);
      return pending;
    }

    // Make the request
    // console.log(`🌐 Cache miss, fetching ${key}`);
    const promise = fetcher()
      .then((data) => {
        // Cache the result
        const expiryTime = ttl || this.getDefaultTTL(key);
        this.cache.set(key, {
          data,
          timestamp: Date.now(),
          expiry: Date.now() + expiryTime
        });
        
        // Remove from pending
        this.pendingRequests.delete(key);
        
        return data;
      })
      .catch((error) => {
        // Remove from pending on error
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store as pending
    this.pendingRequests.set(key, promise);
    
    return promise;
  }

  /**
   * Batch multiple requests together
   */
  async getBatched<T>(
    batchKey: string,
    requestKey: string,
    batchFetcher: (keys: string[]) => Promise<Record<string, T>>,
    delay: number = 50
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      // Add to batch queue
      const queue = this.batchQueue.get(batchKey) || [];
      queue.push({
        key: requestKey,
        resolve,
        reject,
        timestamp: Date.now()
      });
      this.batchQueue.set(batchKey, queue);

      // Clear existing timer
      const existingTimer = this.batchTimers.get(batchKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer to process batch
      const timer = setTimeout(() => {
        this.processBatch(batchKey, batchFetcher);
      }, delay);
      
      this.batchTimers.set(batchKey, timer);
    });
  }

  /**
   * Process a batch of requests
   */
  private async processBatch<T>(
    batchKey: string,
    batchFetcher: (keys: string[]) => Promise<Record<string, T>>
  ) {
    const queue = this.batchQueue.get(batchKey) || [];
    if (queue.length === 0) return;

    // Clear the queue
    this.batchQueue.delete(batchKey);
    this.batchTimers.delete(batchKey);

          // console.log(`📦 Processing batch ${batchKey} with ${queue.length} requests`);

    const keys = queue.map(req => req.key);
    
    try {
      const results = await batchFetcher(keys);
      
      // Resolve all requests
      queue.forEach(req => {
        const result = results[req.key];
        if (result !== undefined) {
          req.resolve(result);
        } else {
          req.reject(new Error(`No result for key: ${req.key}`));
        }
      });
    } catch (error) {
      // Reject all requests
      queue.forEach(req => req.reject(error));
    }
  }

  /**
   * Invalidate cache entries
   */
  invalidateCache(pattern?: string) {
    if (pattern) {
      // Remove specific pattern
      const keysToDelete = Array.from(this.cache.keys()).filter(key => key.includes(pattern));
      keysToDelete.forEach(key => this.cache.delete(key));
              // console.log(`🗑️ Invalidated cache entries matching: ${pattern}`);
    } else {
      // Clear all cache
      this.cache.clear();
      // console.log('🗑️ Cleared all cache');
    }
  }

  /**
   * Synchronously get cached data if available (doesn't make network requests)
   */
  getCachedSync<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.expiry) {
      return cached.data;
    }
    return null;
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    const entries = Array.from(this.cache.entries());
    const now = Date.now();
    
    const stats = {
      total: entries.length,
      expired: entries.filter(([_, entry]) => now >= entry.expiry).length,
      fresh: entries.filter(([_, entry]) => now < entry.expiry).length,
      size: this.cache.size,
      pending: this.pendingRequests.size
    };
    
    return stats;
  }

  /**
   * Clean up expired cache entries
   */
  cleanupCache() {
    const now = Date.now();
    let cleaned = 0;
    
    const keysToDelete: string[] = [];
    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      if (now >= entry.expiry) {
        keysToDelete.push(key);
        cleaned++;
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
    
    if (cleaned > 0) {
      // console.log(`🧹 Cleaned up ${cleaned} expired cache entries`);
    }
  }

  /**
   * Get default TTL for cache key
   */
  private getDefaultTTL(key: string): number {
    for (const [type, ttl] of Object.entries(this.cacheTTL)) {
      if (key.includes(type)) {
        return ttl;
      }
    }
    return 5 * 60 * 1000; // Default 5 minutes
  }
}

// Create singleton instance
export const apiOptimizer = new ApiOptimizer();

// Optimized API wrappers
export const optimizedApi = {
  /**
   * Get videos with caching
   */
  getVideos: async () => {
    return apiOptimizer.getCached('videos', () => videoApi.getVideos());
  },

  /**
   * Get video with caching
   */
  getVideo: async (videoId: string) => {
    return apiOptimizer.getCached(`video_${videoId}`, () => videoApi.getVideo(videoId));
  },

  /**
   * Get video status with short cache
   */
  getVideoStatus: async (videoId: string) => {
    return apiOptimizer.getCached(
      `video_status_${videoId}`, 
      () => videoApi.getVideoStatus(videoId),
      30 * 1000 // 30 second cache for status
    );
  },

  /**
   * Get clips with caching
   */
  getClips: async (videoId?: string) => {
    const key = videoId ? `clips_${videoId}` : 'clips_all';
    return apiOptimizer.getCached(key, () => clipApi.getClips(videoId));
  },

  /**
   * Get video clips with caching (specific endpoint for video clips)
   */
  getVideoClips: async (videoId: string) => {
    const key = `video_clips_${videoId}`;
    return apiOptimizer.getCached(key, () => clipApi.getVideoClips(videoId));
  },

  /**
   * Get individual clip with caching
   */
  getClip: async (clipId: string) => {
    return apiOptimizer.getCached(`clip_${clipId}`, () => clipApi.getClip(clipId));
  },

  /**
   * Get stats with caching
   */
  getStats: async () => {
    return apiOptimizer.getCached('stats', () => videoApi.getStats());
  },

  /**
   * Get analytics dashboard data with caching
   */
  getAnalytics: async (days: string = '30') => {
    return apiOptimizer.getCached(
      `analytics_${days}`, 
      async () => {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('Authentication required');
        }

        const token = await user.getIdToken();
        const response = await fetch(`/api/v1/analytics/dashboard?days=${days}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch analytics');
        }

        const result = await response.json();
        return result.data;
      },
      10 * 60 * 1000 // 10 minute cache for analytics
    );
  },

  /**
   * Batch video status checks
   */
  getBatchedVideoStatus: async (videoIds: string[]) => {
    const results: Record<string, any> = {};
    
    // Use cached results where available, batch the rest
    const uncachedIds: string[] = [];
    
    for (const videoId of videoIds) {
      const cached = apiOptimizer['cache'].get(`video_status_${videoId}`);
      if (cached && Date.now() < cached.expiry) {
        results[videoId] = cached.data;
      } else {
        uncachedIds.push(videoId);
      }
    }
    
    // Batch fetch uncached videos
    if (uncachedIds.length > 0) {
      const batchResults = await Promise.allSettled(
        uncachedIds.map(id => optimizedApi.getVideoStatus(id))
      );
      
      batchResults.forEach((result, index) => {
        const videoId = uncachedIds[index];
        if (result.status === 'fulfilled') {
          results[videoId] = result.value;
        }
      });
    }
    
    return results;
  },

  /**
   * Prefetch data for better UX
   */
  prefetch: {
    dashboardData: async () => {
      // Only prefetch dashboard data for authenticated users
      const user = auth.currentUser;
      if (!user) {
        // console.log('📋 Skipping dashboard data prefetch - user not authenticated');
        return;
      }
      
      // Prefetch common dashboard data
      const promises = [
        optimizedApi.getVideos(),
        optimizedApi.getStats(),
        optimizedApi.getClips()
      ];
      
      // Don't await - just start the requests
      Promise.allSettled(promises).then(() => {
        // console.log('📋 Dashboard data prefetched');
      });
    },

    analyticsData: async (days: string = '30') => {
      // Prefetch analytics data
      optimizedApi.getAnalytics(days).then(() => {
        // console.log(`📋 Analytics data (${days} days) prefetched`);
      }).catch(() => {
        // Silently fail for prefetch
      });
    },

    videoDetails: async (videoId: string) => {
      // Prefetch video and its clips
      const promises = [
        optimizedApi.getVideo(videoId),
        optimizedApi.getClips(videoId)
      ];
      
      Promise.allSettled(promises).then(() => {
        // console.log(`📋 Video ${videoId} data prefetched`);
      });
    },

    clipDetails: async (clipId: string) => {
      // Prefetch clip details
      optimizedApi.getClip(clipId).then(() => {
        // console.log(`📋 Clip ${clipId} data prefetched`);
      }).catch(() => {
        // Silently fail for prefetch
      });
    }
  },

  /**
   * Cache management
   */
  cache: {
    invalidate: (pattern?: string) => apiOptimizer.invalidateCache(pattern),
    invalidateAnalytics: () => apiOptimizer.invalidateCache('analytics'),
    invalidateVideos: () => {
      // When videos change, also invalidate analytics and stats
      apiOptimizer.invalidateCache('videos');
      apiOptimizer.invalidateCache('analytics');
      apiOptimizer.invalidateCache('stats');
    },
    stats: () => apiOptimizer.getCacheStats(),
    cleanup: () => apiOptimizer.cleanupCache(),
    // Sync cache access for pre-populating UI state
    getVideosSync: () => apiOptimizer.getCachedSync('videos'),
    getStatsSync: () => apiOptimizer.getCachedSync('stats'),
    getClipsSync: (videoId?: string) => {
      const key = videoId ? `clips_${videoId}` : 'clips_all';
      return apiOptimizer.getCachedSync(key);
    },
    getVideoClipsSync: (videoId: string) => {
      const key = `video_clips_${videoId}`;
      return apiOptimizer.getCachedSync(key);
    },
    getVideoSync: (videoId: string) => apiOptimizer.getCachedSync(`video_${videoId}`),
    getClipSync: (clipId: string) => apiOptimizer.getCachedSync(`clip_${clipId}`),
    getAnalyticsSync: (days: string = '30') => apiOptimizer.getCachedSync(`analytics_${days}`)
  }
};

// Auto cleanup cache every 5 minutes
setInterval(() => {
  optimizedApi.cache.cleanup();
}, 5 * 60 * 1000);

// Enhanced request batching for multiple similar requests
export const batchRequests = {
  /**
   * Batch multiple video status checks
   */
  videoStatus: async (videoIds: string[]) => {
    const batchFetcher = async (ids: string[]) => {
      const results: Record<string, any> = {};
      
      // Make concurrent requests (better than sequential)
      const promises = ids.map(async (id) => {
        try {
          // Use status endpoint so widgets get progress/error_message
          const status = await videoApi.getVideoStatus(id);
          results[id] = status;
        } catch (error) {
          // console.warn(`Failed to fetch status ${id}:`, error);
          results[id] = null;
        }
      });
      
      await Promise.allSettled(promises);
      return results;
    };

    const results: Record<string, any> = {};
    for (const videoId of videoIds) {
      try {
        results[videoId] = await apiOptimizer.getBatched(
          'video_status_batch',
          videoId,
          batchFetcher,
          100 // 100ms batch delay
        );
      } catch (error) {
        results[videoId] = null;
      }
    }
    
    return results;
  }
};

// Export for debugging
if (process.env.NODE_ENV === 'development') {
  (window as any).apiOptimizer = apiOptimizer;
  (window as any).optimizedApi = optimizedApi;
} 